import PropTypes from 'prop-types';
import React from 'react';
import { Provider } from '@dashboard-experience/react-flagr';
import Cookie from 'js-cookie';
import '@dashboard-experience/mastodon/lib/styles.css';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { isInternational as getIsInternational } from '../lib/helpers';
import { ACCOUNT_URI_COOKIE_NAME, ENV, FLAGR_URL } from '../constants';
import { CustomIncidentProvider } from '../providers/CustomIncidentProvider';
import {
  useInternational,
  useSetInternational,
} from '../providers/InternationalProvider';
import Header from './Header';
import Footer from './Footer';

const bothAccountURIsPresentAndDistinct = (cookieAccountURI, accountURI) => {
  return !!(cookieAccountURI && accountURI && cookieAccountURI !== accountURI);
};

export const getAccountUri = (accountURI = '', viewingLoginScreen = false) => {
  if (viewingLoginScreen) {
    Cookie.remove(ACCOUNT_URI_COOKIE_NAME);
    return '';
  }

  const cookieAccountURI = Cookie.get(ACCOUNT_URI_COOKIE_NAME) || '';
  if (bothAccountURIsPresentAndDistinct(cookieAccountURI, accountURI)) {
    Cookie.set(ACCOUNT_URI_COOKIE_NAME, accountURI);
    return accountURI;
  }
  if (!cookieAccountURI) {
    Cookie.set(ACCOUNT_URI_COOKIE_NAME, accountURI);
    return accountURI;
  }
  return cookieAccountURI;
};

const getReduxState = state => {
  const firstReport = state?.portal?.reports[0];
  return {
    portalIsInternational: getIsInternational(state.portal),
    accountUri: firstReport?.account?.uriName,
    candidateEmail: firstReport?.candidate?.email,
  };
};

const App = ({ children }) => {
  const { portalIsInternational, accountUri, candidateEmail } =
    useSelector(getReduxState);
  const international = useInternational();
  const setInternational = useSetInternational();
  const isInternational = international || portalIsInternational;
  const { hash } = useLocation();
  const isLogin = hash?.indexOf('login') >= 0;
  const accountURI = getAccountUri(accountUri, isLogin);

  React.useEffect(() => {
    setInternational(isInternational);
  }, [isInternational, setInternational]);

  return (
    <Provider
      url={FLAGR_URL || ''}
      entityId='env'
      entityContext={{
        env: ENV,
        account_uri: accountURI,
        locationHost: window.location.host,
        candidateEmail,
      }}
    >
      <CustomIncidentProvider>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Header isInternational={isInternational} />
          {children}
          <Footer international={isInternational} />
        </div>
      </CustomIncidentProvider>
    </Provider>
  );
};

App.propTypes = {
  children: PropTypes.node,
};

App.defaultProps = {
  children: [],
};

export default App;
